import React, { useEffect, useState } from 'react';
import { useAppShell } from '../contexts/AppShellContext';
import { useAuth } from '../contexts/AuthContext';
import { updateProfile } from '../services/userService';
import { teamLabel, scopeOf, roleLabel } from '../utils/rbac';

const SCOPE_LABEL: Record<string, string> = {
  platform: 'Nexus',
  org: 'Organization',
  team: 'Team',
};

function Field({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <div className="text-xs font-semibold text-muted uppercase tracking-wide mb-1">{label}</div>
      <div className="text-sm text-ink">{value}</div>
    </div>
  );
}

export function ProfilePage() {
  const { setTopbar } = useAppShell();
  const { user, setUser } = useAuth();
  useEffect(() => { setTopbar('Profile', 'Your account and access'); }, [setTopbar]);

  const [name, setName] = useState(user?.name ?? '');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null);

  if (!user) return null;
  const scope = scopeOf(user);

  async function onSave(e: React.FormEvent) {
    e.preventDefault();
    setMsg(null);
    if (newPassword && newPassword !== confirmPassword) {
      setMsg({ kind: 'err', text: 'New passwords do not match.' });
      return;
    }
    if (newPassword && !currentPassword) {
      setMsg({ kind: 'err', text: 'Enter your current password to change it.' });
      return;
    }
    setSaving(true);
    try {
      const updated = await updateProfile({
        name: name.trim() || undefined,
        current_password: currentPassword || undefined,
        new_password: newPassword || undefined,
      });
      setUser(updated);
      setCurrentPassword(''); setNewPassword(''); setConfirmPassword('');
      setMsg({ kind: 'ok', text: 'Profile updated.' });
    } catch (err: any) {
      setMsg({ kind: 'err', text: err?.response?.data?.detail || 'Could not update profile.' });
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="h-full overflow-y-auto px-8 py-6">
      <div className="max-w-2xl space-y-6">
        {/* Binding (read-only — only an admin can change it) */}
        <section className="bg-white border border-trim rounded-2xl p-6">
          <h2 className="text-sm font-semibold text-ink mb-4">Access</h2>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Email" value={user.username} />
            <Field label="Role" value={roleLabel(user.role)} />
            <Field label="Scope" value={SCOPE_LABEL[scope]} />
            <Field label="Team" value={user.team_id ? teamLabel(user.team_id) : '—'} />
          </div>
          <p className="text-xs text-muted mt-4">
            Your role and team are managed by an administrator.
          </p>
        </section>

        {/* Editable profile */}
        <form onSubmit={onSave} className="bg-white border border-trim rounded-2xl p-6 space-y-4">
          <h2 className="text-sm font-semibold text-ink">Profile</h2>

          <div>
            <label className="text-xs font-semibold text-muted uppercase tracking-wide">Name</label>
            <input
              className="w-full px-3 py-2 rounded-lg border border-trim text-sm text-ink outline-none focus:border-teal-400 bg-white mt-1"
              value={name}
              onChange={e => setName(e.target.value)}
            />
          </div>

          <div className="h-px bg-trim" />
          <div className="text-xs font-semibold text-muted uppercase tracking-wide">Change password</div>

          <div>
            <label className="text-xs text-muted">Current password</label>
            <input
              type="password" autoComplete="current-password"
              className="w-full px-3 py-2 rounded-lg border border-trim text-sm text-ink outline-none focus:border-teal-400 bg-white mt-1"
              value={currentPassword}
              onChange={e => setCurrentPassword(e.target.value)}
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-muted">New password</label>
              <input
                type="password" autoComplete="new-password"
                className="w-full px-3 py-2 rounded-lg border border-trim text-sm text-ink outline-none focus:border-teal-400 bg-white mt-1"
                value={newPassword}
                onChange={e => setNewPassword(e.target.value)}
              />
            </div>
            <div>
              <label className="text-xs text-muted">Confirm new password</label>
              <input
                type="password" autoComplete="new-password"
                className="w-full px-3 py-2 rounded-lg border border-trim text-sm text-ink outline-none focus:border-teal-400 bg-white mt-1"
                value={confirmPassword}
                onChange={e => setConfirmPassword(e.target.value)}
              />
            </div>
          </div>

          {msg && (
            <div className={`text-sm ${msg.kind === 'ok' ? 'text-green-700' : 'text-red-600'}`}>{msg.text}</div>
          )}

          <div className="flex justify-end">
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 rounded-lg text-sm font-semibold text-white bg-gradient-to-r from-teal-500 to-cyan-500 hover:shadow-lg disabled:opacity-50"
            >
              {saving ? 'Saving…' : 'Save changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
